import { useEffect, useRef, useCallback, useReducer } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken, positionUnwinderAxiosApi } from 'src/Config/axiosApi';
import {
  EnrichedRuleStatus,
  OrdersResponse,
  UnwindingPageState,
} from '../types/unwinding.types';
import {
  LatencySample,
  recordReceived,
  useLatencyTracker,
} from './useLatencyTracker';
export type { EnrichedRuleStatus } from '../types/unwinding.types';

export interface UnwindExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface UnwindErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

type Action =
  | { type: 'STATUS'; payload: EnrichedRuleStatus[] }
  | { type: 'STATUS_UPDATE'; payload: EnrichedRuleStatus[] }
  | { type: 'EXECUTION'; payload: UnwindExecutionEvent }
  | { type: 'ERROR'; payload: UnwindErrorEvent }
  | { type: 'ORDERS'; payload: OrdersResponse };

interface State {
  /** Map of ruleId -> live order state */
  states: Map<string, EnrichedRuleStatus>;
  /** Most recent execution event (for toasts/notifications) */
  lastExecution: UnwindExecutionEvent | null;
  /** Most recent error event */
  lastError: UnwindErrorEvent | null;
  orders: OrdersResponse | null;
}

const initialState: State = {
  states: new Map(),
  lastExecution: null,
  lastError: null,
  orders: null,
};

/**
 * Strip the server's `_meta` envelope before passing into the reducer.
 * Server wraps non-array payloads as `{ ...payload, _meta }` and array
 * payloads as `{ data: payload, _meta }`. Reducers don't need to know.
 */
/**
 * Phase 6 — project `UnwindingPageState` back into the legacy
 * `EnrichedRuleStatus` shape so existing row components keep working
 * during the bridging release. Position-side dictates which (bid, ask)
 * pair is displayed prominently. When `rule === null`, rule-scoped
 * fields use the same `state: 'no_rule'` defaults the legacy backend
 * produced for position-only rows.
 */
function projectUnwindingToLegacy(
  p: UnwindingPageState,
): EnrichedRuleStatus {
  const m = p.market;
  const r = p.rule;
  const pos = p.position;
  const polySide = pos.polyPosition?.side ?? 'yes';
  const kalshiSide = pos.kalshiPosition?.side ?? 'yes';
  return {
    ruleId: r?.ruleId ?? p.pairId,
    pairId: p.pairId,
    state: r?.state ?? 'no_rule',
    makerPlatform: r?.makerPlatform ?? 'kalshi',
    makerOrderId: r?.makerOrderId ?? null,
    makerPrice: r?.makerPrice ?? null,
    makerFilledQty: r?.makerFilledQty ?? 0,
    makerFilledPrice: r?.makerFilledPrice ?? 0,
    counterOrderId: r?.counterOrderId ?? null,
    counterFilledQty: r?.counterFilledQty ?? 0,
    lastStateChange: r?.lastStateChange ?? new Date().toISOString(),
    errorMessage: r?.errorMessage ?? null,
    pairName: m.pairName,
    expiryDate: m.expiryDate,
    daysToExpiry: m.daysToExpiry,
    polyBid: polySide === 'no' ? m.polyNoBid : m.polyYesBid,
    polyAsk: polySide === 'no' ? m.polyNoAsk : m.polyYesAsk,
    kalshiBid: kalshiSide === 'no' ? m.kalshiNoBid : m.kalshiYesBid,
    kalshiAsk: kalshiSide === 'no' ? m.kalshiNoAsk : m.kalshiYesAsk,
    simulation: r?.simulation ?? {
      polyAsMaker: null,
      kalshiAsMaker: null,
    },
    kalshiConfig: r?.kalshiConfig ?? null,
    polymarketConfig: r?.polymarketConfig ?? null,
    maxVolumeContracts: r?.maxVolumeContracts ?? 0,
    volumePeriod: r?.volumePeriod ?? 'day',
    volumeUsed: r?.volumeUsed ?? 0,
    volumeRemaining: r?.volumeRemaining ?? 0,
    kalshiMarketUrl: m.kalshiMarketUrl,
    polymarketMarketUrl: m.polymarketMarketUrl,
    simTna: r?.simTna,
    simSize: r?.simSize,
    simMaxVolume: r?.simMaxVolume,
    appliedSimTna: r?.appliedSimTna,
    appliedSimSize: r?.appliedSimSize,
    appliedSimMaxVolume: r?.appliedSimMaxVolume,
    hasPendingSim: r?.hasPendingSim,
    // Per-pair kill switch is owned by PositionGroup (backend
    // projection), independent of whether a rule exists. Treat missing
    // as ON so an old payload doesn't render the switch as OFF.
    unwinderEnabled: pos.unwinderEnabled !== false,
  };
}

function unwrap<T>(message: unknown): T {
  if (Array.isArray(message)) return message as T;
  if (
    message &&
    typeof message === 'object' &&
    '_meta' in (message as object)
  ) {
    const obj = message as Record<string, unknown> & { _meta?: unknown };
    if ('data' in obj) return obj.data as T;
    const rest = { ...obj };
    delete rest._meta;
    return rest as T;
  }
  return message as T;
}

/**
 * `unwinderEnabled` is a pair-scoped kill switch carried ONLY by the
 * Phase-6 `unwinding_state` / `unwinding_state_update` stream (which
 * `projectUnwindingToLegacy` always sets to a definite boolean). The
 * legacy `unwind_status` / `unwind_status_update` stream — still the
 * unwinder's COMPREHENSIVE update path, so we can't stop listening to it —
 * and the `/states` REST seed both OMIT the field. Every stream writes
 * this same Map, so a legacy event arriving with `unwinderEnabled:
 * undefined` would wipe the value the preceding `unwinding_state*` event
 * set; with both streams firing on every broadcast cycle the per-pair
 * switch then visibly FLIPS (false → undefined→renders ON → false → …).
 * Preserve the last known value whenever an incoming entry doesn't carry
 * the field — only the `unwinding_state*` stream may ever change it.
 */
function withPreservedUnwinderEnabled(
  prior: Map<string, EnrichedRuleStatus>,
  incoming: EnrichedRuleStatus,
): EnrichedRuleStatus {
  if (incoming.unwinderEnabled !== undefined) return incoming;
  const prev =
    prior.get(incoming.ruleId)?.unwinderEnabled ??
    (incoming.pairId
      ? prior.get(incoming.pairId)?.unwinderEnabled
      : undefined);
  return prev === undefined
    ? incoming
    : { ...incoming, unwinderEnabled: prev };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'STATUS': {
      // Full snapshot — replace entire map
      const newMap = new Map<string, EnrichedRuleStatus>();
      for (const raw of action.payload) {
        const s = withPreservedUnwinderEnabled(state.states, raw);
        newMap.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) newMap.set(s.pairId, s);
      }
      return { ...state, states: newMap };
    }
    case 'STATUS_UPDATE': {
      // Partial update — merge into existing map
      const merged = new Map(state.states);
      for (const raw of action.payload) {
        const s = withPreservedUnwinderEnabled(state.states, raw);
        merged.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) merged.set(s.pairId, s);
      }
      return { ...state, states: merged };
    }
    case 'EXECUTION':
      return { ...state, lastExecution: action.payload };
    case 'ERROR':
      return { ...state, lastError: action.payload };
    case 'ORDERS':
      return { ...state, orders: action.payload };
    default:
      return state;
  }
}

export function useUnwindingSocket() {
  const wsUrl = import.meta.env.VITE_POSITION_UNWINDER_URL;
  const accessToken = _getToken();

  const { socket } = useSocket(
    (wsUrl || '') + '/unwind',
    {
      enabled: !!wsUrl && !!accessToken,
      transports: ['websocket'],
      // Pass auth as a callback so socket.io-client reads the current
      // token on every (re)connection. This ensures logout/refresh
      // updates propagate and we never reconnect with a stale token.
      auth: (cb: (data: { token: string }) => void) =>
        cb({ token: _getToken() }),
    }
  );

  const [state, dispatch] = useReducer(reducer, initialState);
  const hasSubscribed = useRef(false);

  // REST seed: fetch the same EnrichedRuleState[] payload the WS broadcasts
  // and inject it into the reducer. Runs on mount and whenever the WS is
  // disconnected — so the table renders market data even when:
  //   - the user just opened the page and the WS handshake is in flight,
  //   - the WS auth fails or the connection is briefly dropped,
  //   - the Predictions subsystem is OFF (the old REST fallback path on
  //     `polyPos.topBid`/`kalshiPos.topBid` from prediction-arbitrage-manager
  //     is undefined in that case — position-unwinder owns the prices now).
  // The live WS updates always override the REST seed via STATUS_UPDATE.
  useEffect(() => {
    if (!wsUrl || !accessToken) return;
    if (socket.connected) return; // WS will deliver the snapshot via subscribe_unwind

    let cancelled = false;
    positionUnwinderAxiosApi
      .get<EnrichedRuleStatus[]>('/states')
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data)) return;
        dispatch({ type: 'STATUS_UPDATE', payload: data });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[UnwindSocket] REST seed failed:', err?.message ?? err);
      });

    return () => {
      cancelled = true;
    };
  }, [wsUrl, accessToken, socket.connected]);

  // Latency tracker: each WS event handler below records the receive
  // timestamp and the first-rAF-after-receipt timestamp. Samples are
  // batched and flushed every 5 s via `latency_report` to the server,
  // which converts them into the wsDeliveryMs / reactRenderMs histograms
  // visible at /unwind/metrics/latency.
  const latencyBuf = useRef<LatencySample[]>([]);
  useLatencyTracker(socket, latencyBuf);

  // Subscribe on every connect/reconnect. `socket` object identity is stable,
  // so depending on it alone can miss the connected transition and skip the
  // initial `subscribe_unwind` forever.
  useEffect(() => {
    const subscribeIfNeeded = () => {
      if (!hasSubscribed.current) {
        socket.emit('subscribe_unwind');
        hasSubscribed.current = true;
      }
    };
    const markDisconnected = () => {
      hasSubscribed.current = false;
    };

    socket.on('connect', subscribeIfNeeded);
    socket.on('disconnect', markDisconnected);

    if (socket.connected) subscribeIfNeeded();
    else markDisconnected();

    return () => {
      socket.off('connect', subscribeIfNeeded);
      socket.off('disconnect', markDisconnected);
    };
  }, [socket, socket.connected]);

  // Listen to events
  const { lastMessage: statusMessage } = useSocketEvent<EnrichedRuleStatus[]>(
    socket,
    'unwind_status'
  );

  const { lastMessage: statusUpdateMessage } = useSocketEvent<EnrichedRuleStatus[]>(
    socket,
    'unwind_status_update'
  );

  const { lastMessage: executionMessage } =
    useSocketEvent<UnwindExecutionEvent>(socket, 'unwind_execution');

  const { lastMessage: errorMessage } = useSocketEvent<UnwindErrorEvent>(
    socket,
    'unwind_error'
  );

  // Handle full snapshot
  useEffect(() => {
    if (statusMessage) {
      recordReceived(latencyBuf.current, 'unwind_status', statusMessage);
      const payload = unwrap<EnrichedRuleStatus[]>(statusMessage);
      if (!Array.isArray(payload)) {
        console.warn('[UnwindSocket] unwind_status non-array payload, ignoring:', payload);
        return;
      }
      dispatch({ type: 'STATUS', payload });
    }
  }, [statusMessage]);

  // Handle partial updates (individual market changes)
  useEffect(() => {
    if (statusUpdateMessage) {
      recordReceived(latencyBuf.current, 'unwind_status_update', statusUpdateMessage);
      const payload = unwrap<EnrichedRuleStatus[]>(statusUpdateMessage);
      if (!Array.isArray(payload)) {
        console.warn('[UnwindSocket] unwind_status_update non-array payload, ignoring:', payload);
        return;
      }
      dispatch({ type: 'STATUS_UPDATE', payload });
    }
  }, [statusUpdateMessage]);

  useEffect(() => {
    if (executionMessage) {
      recordReceived(latencyBuf.current, 'unwind_execution', executionMessage);
      dispatch({ type: 'EXECUTION', payload: unwrap<UnwindExecutionEvent>(executionMessage) });
    }
  }, [executionMessage]);

  useEffect(() => {
    if (errorMessage) {
      recordReceived(latencyBuf.current, 'unwind_error', errorMessage);
      dispatch({ type: 'ERROR', payload: unwrap<UnwindErrorEvent>(errorMessage) });
    }
  }, [errorMessage]);

  // ── Phase 6 — split-shape listeners. Backend emits these alongside
  //    the legacy `unwind_status` / `unwind_status_update` for one
  //    bridging release. We project envelopes back to the legacy shape
  //    so the existing reducer + row components keep working. Phase 7
  //    removes the legacy listeners + projection.
  const { lastMessage: unwindingStateMessage } = useSocketEvent<
    UnwindingPageState[]
  >(socket, 'unwinding_state');
  const { lastMessage: unwindingStateUpdateMessage } = useSocketEvent<
    UnwindingPageState[]
  >(socket, 'unwinding_state_update');

  useEffect(() => {
    if (unwindingStateMessage) {
      recordReceived(
        latencyBuf.current,
        'unwinding_state',
        unwindingStateMessage,
      );
      const payload = unwrap<UnwindingPageState[]>(unwindingStateMessage);
      if (!Array.isArray(payload)) return;
      dispatch({
        type: 'STATUS',
        payload: payload.map(projectUnwindingToLegacy),
      });
    }
  }, [unwindingStateMessage]);

  useEffect(() => {
    if (unwindingStateUpdateMessage) {
      recordReceived(
        latencyBuf.current,
        'unwinding_state_update',
        unwindingStateUpdateMessage,
      );
      const payload = unwrap<UnwindingPageState[]>(unwindingStateUpdateMessage);
      if (!Array.isArray(payload)) return;
      dispatch({
        type: 'STATUS_UPDATE',
        payload: payload.map(projectUnwindingToLegacy),
      });
    }
  }, [unwindingStateUpdateMessage]);

  const { lastMessage: ordersMessage } = useSocketEvent<OrdersResponse>(
    socket,
    'unwind_orders'
  );

  useEffect(() => {
    if (ordersMessage) {
      recordReceived(latencyBuf.current, 'unwind_orders', ordersMessage);
      dispatch({ type: 'ORDERS', payload: unwrap<OrdersResponse>(ordersMessage) });
    }
  }, [ordersMessage]);

  // Stable getState identity — points at a ref that always tracks the
  // latest Map. Without this, every WS tick re-creates `state.states`
  // (the reducer rebuilds the Map for `STATUS_UPDATE`), which flips
  // `getState`'s identity, which busts every consumer's `useMemo` that
  // depends on it (notably the table's `sortedGroups` memo). Net effect
  // before: every WS tick re-ran the full sort + map + cell render,
  // even for rules whose state didn't actually change.
  //
  // Pattern: a ref that mirrors the Map, plus a getState callback with
  // empty deps. The Map identity still flips inside `state.states`
  // (consumers that DO want re-renders on every change keep reading
  // `states`), but `getState` is now a stable function reference that
  // any descendant `useMemo` can include in its deps without busting.
  const statesRef = useRef(state.states);
  statesRef.current = state.states;
  const getState = useCallback(
    (ruleId: string): EnrichedRuleStatus | undefined =>
      statesRef.current.get(ruleId),
    [],
  );

  /** Manually inject enriched states (e.g., from REST response as WS fallback) */
  const injectStates = useCallback((states: EnrichedRuleStatus[]) => {
    if (!Array.isArray(states)) {
      console.warn('[UnwindSocket] injectStates called with non-array, ignoring:', states);
      return;
    }
    dispatch({ type: 'STATUS_UPDATE', payload: states });
  }, []);

  return {
    isConnected: socket.connected,
    states: state.states,
    getState,
    injectStates,
    lastExecution: state.lastExecution,
    lastError: state.lastError,
    orders: state.orders,
  };
}
